import { PlusOutlined } from "@ant-design/icons";
import Swal from "sweetalert2";
import { Button, Form, Input, Modal, Select, Table, Row, Col } from "antd";
import { getColumns } from "./modules/getColumns";
import { usePolicies } from "./modules/usePolicies";
import { formatCurrency } from "../../../utils/formatCurrency";

const { Option } = Select;

const PoliciesScreen = () => {
  const {
    policies,
    formData,
    editingPolicy,
    isModalVisible,
    extraBedPrice,
    setExtraBedPrice,
    setIsModalVisible,
    handleCancellationRuleChange,
    handleChildrenRuleChange,
    handleModalOk,
    handleCreate,
    handleEdit,
    handleDelete,
    handleExtraBedPriceSubmit,
    handleSaveChildrenPolicy,
    handleChildrenAgeRuleChange,
  } = usePolicies();

  const columns = getColumns({
    handleEdit,
    handleDelete,
  });

  return (
    <div className="container">
      <h1>Policies Management</h1>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={handleCreate}
        style={{ marginBottom: 16 }}
      >
        Create Cancellation Policy
      </Button>

      <Table
        dataSource={policies}
        columns={columns}
        rowKey="_id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingPolicy ? "Edit Policy" : "Create Policy"}
        visible={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
      >
        <Form layout="vertical">
          {/* Cancellation Policy */}
          <Form.Item
            label="Is there a period when the guest can cancel free of charge?"
            required
          >
            <Select
              value={formData.cancellationRule[0].chargeType}
              onChange={(value) =>
                handleCancellationRuleChange("chargeType", value)
              }
            >
              <Option value="Flexible">Flexible</Option>
              <Option value="Non-refundable">Non-refundable</Option>
            </Select>
          </Form.Item>

          {formData.cancellationRule[0].chargeType === "Flexible" && (
            <Form.Item
              label="How many day(s) before arrival can the guest cancel free of charge?"
              required
            >
              <Input
                type="number"
                value={formData.cancellationRule[0].daysBeforeCheckin}
                onChange={(e) =>
                  handleCancellationRuleChange(
                    "daysBeforeCheckin",
                    e.target.value
                  )
                }
              />
            </Form.Item>
          )}

          <Form.Item
            label="How much is the guest charged for total booking if cancel (in %)?"
            required
          >
            <Input
              type="number"
              value={formData.cancellationRule[0].chargePercentage}
              onChange={(e) =>
                handleCancellationRuleChange("chargePercentage", e.target.value)
              }
            />
          </Form.Item>

          <Form.Item label="Max Age for Free Stay" required>
            <Input
              type="number"
              value={formData.childrenRule.maxAge}
              onChange={(e) =>
                handleChildrenRuleChange("maxAge", e.target.value)
              }
            />
          </Form.Item>

          <Form.Item label="Free Stay" required>
            <Select
              value={formData.childrenRule.freeStay}
              onChange={(value) => handleChildrenRuleChange("freeStay", value)}
            >
              <Option value={true}>Yes</Option>
              <Option value={false}>No</Option>
            </Select>
          </Form.Item>

          {!formData.childrenRule.freeStay && (
            <Form.Item label="Additional Child Charge" required>
              <Input
                type="number"
                value={formData.childrenRule.additionalChildCharge}
                onChange={(e) =>
                  handleChildrenRuleChange(
                    "additionalChildCharge",
                    e.target.value
                  )
                }
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* Children Age Policy: two-part layout */}
      <div className="mt-2 mb-5">
        <h2>Children Age Policy</h2>

        {/* Stage 1: Free children configuration */}
        <div
          style={{ padding: 12, border: "1px solid #eee", marginBottom: 12 }}
        >
          <h3 style={{ marginTop: 0 }}>Stage 1 — Free children (from 1 to)</h3>
          <Row gutter={16} align="middle">
            <Col span={6}>
              <Form.Item label="Free until (years):" required>
                <Select
                  value={formData.childrenAgeRule?.freeAge}
                  onChange={(value) => {
                    handleChildrenAgeRuleChange("freeAge", value);
                    const currentMax = formData.childrenAgeRule?.maxAge;
                    if (!currentMax || value >= currentMax) {
                      handleChildrenAgeRuleChange("maxAge", value + 1);
                    }
                  }}
                  style={{ width: "100%" }}
                >
                  {Array.from({ length: 6 }, (_, i) => i + 1).map((age) => (
                    <Option key={age} value={age}>
                      {age}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* Stage 2: Extra bed price configuration */}
        <div style={{ padding: 12, border: "1px solid #eee" }}>
          <h3 style={{ marginTop: 0 }}>
            Stage 2 — Extra bed price (from{" "}
            {(formData.childrenAgeRule?.freeAge || 0) + 1} to
          </h3>
          <Row gutter={24} align="middle">
            <Col span={8}>
              <Form.Item label="Extra-bed charged until (years):" required>
                <Select
                  value={formData.childrenAgeRule?.maxAge}
                  onChange={(value) =>
                    handleChildrenAgeRuleChange("maxAge", value)
                  }
                  style={{ width: "100%" }}
                >
                  {Array.from({ length: 17 }, (_, i) => i + 1)
                    .filter(
                      (age) => age > (formData.childrenAgeRule?.freeAge || 0)
                    )
                    .map((age) => (
                      <Option key={age} value={age}>
                        {age}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="Extra price (per night):" required>
                <Input
                  type="text"
                  value={
                    formData.childrenAgeRule?.extraPrice
                      ? formatCurrency(
                          Number(
                            formData.childrenAgeRule.extraPrice
                              .toString()
                              .replace(/\./g, "")
                          )
                        )
                      : ""
                  }
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/\./g, "");
                    const value = rawValue ? parseInt(rawValue, 10) : 0;
                    if (value > 0 || rawValue === "") {
                      handleChildrenAgeRuleChange(
                        "extraPrice",
                        value.toString()
                      );
                    } else {
                      Swal.fire(
                        "Invalid Input",
                        `Extra bed price must be greater than 0.`,
                        "warning"
                      );
                    }
                  }}
                  style={{ width: "160px", marginRight: "10px" }}
                />
                <span>VND</span>
              </Form.Item>
            </Col>

            <Col span={4}>
              <Button type="primary" onClick={handleSaveChildrenPolicy}>
                Save
              </Button>
            </Col>
          </Row>
        </div>
      </div>

      <div className="mt-2 mb-5">
        <h2>Extra Bed Price</h2>
        <Input
          type="text"
          value={
            extraBedPrice
              ? formatCurrency(
                  Number(extraBedPrice.toString().replace(/\./g, ""))
                )
              : ""
          }
          onChange={(e) => {
            const rawValue = e.target.value.replace(/\./g, "");
            const value = rawValue ? parseInt(rawValue, 10) : 0;

            if (value > 0 || rawValue === "") {
              setExtraBedPrice(value.toString());
            } else {
              Swal.fire(
                "Invalid Input",
                `Extra bed price must be greater than 0.`,
                "warning"
              );
            }
          }}
          style={{ width: "200px", marginRight: "10px" }}
        />
        <span>VND per night</span>
        <Button
          type="primary"
          onClick={handleExtraBedPriceSubmit}
          style={{ marginLeft: "16px" }}
        >
          Update Price
        </Button>
      </div>
    </div>
  );
};

export default PoliciesScreen;
